import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../../styles/user/CreateUser.css';
import axios from 'axios';
import { getRecaptchaToken, loadRecaptcha } from '../../utils/recaptcha';

import { tryOpenAppThenStore, APP_STORE_URL, PLAY_STORE_URL } from '../../utils/appStoreLinks';

const CreateUser = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const [userData, setUserData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    sex: '',
    age: '',
    level: '',
    businessCode: '',
    discountCode: ''
  });
  const [businessCodeValid, setBusinessCodeValid] = useState(null);
  const [businessCodeInfo, setBusinessCodeInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [checkoutTierId, setCheckoutTierId] = useState('');
  const [checkoutInfo, setCheckoutInfo] = useState({
    productName: '',
    tierLabel: '',
    priceAmount: '',
    discountPercentage: ''
  });
  const [autoCheckoutLoading, setAutoCheckoutLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Pre-load reCAPTCHA script when component mounts
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      loadRecaptcha().catch(err => {
        console.warn('reCAPTCHA pre-load failed:', err);
      });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const discountCodeFromPath = params.get('discount_code');
    const priceTierId = params.get('price_tier_id');
    const productName = params.get('product_name');
    const tierLabel = params.get('tier_label');
    const priceAmount = params.get('price_amount');
    const discountPercentage = params.get('discount_percentage');
    if (discountCodeFromPath && discountCodeFromPath.trim() !== '') {
      const normalizedCode = discountCodeFromPath
        .trim()
        .replace(/^"+|"+$/g, '')
        .toUpperCase();
      setUserData((prev) => ({
        ...prev,
        discountCode: normalizedCode
      }));
    }
    setCheckoutTierId(priceTierId ? String(priceTierId).trim() : '');
    setCheckoutInfo({
      productName: productName ? String(productName).trim() : '',
      tierLabel: tierLabel ? String(tierLabel).trim() : '',
      priceAmount: priceAmount ? String(priceAmount).trim() : '',
      discountPercentage: discountPercentage ? String(discountPercentage).trim() : ''
    });
  }, [location.search]);

  const buildLoginUrlWithCheckoutParams = () => {
    const params = new URLSearchParams();
    if (userData.discountCode) params.set('discount_code', userData.discountCode);
    if (checkoutTierId) params.set('price_tier_id', checkoutTierId);
    if (checkoutInfo.productName) params.set('product_name', checkoutInfo.productName);
    if (checkoutInfo.tierLabel) params.set('tier_label', checkoutInfo.tierLabel);
    if (checkoutInfo.priceAmount) params.set('price_amount', checkoutInfo.priceAmount);
    if (checkoutInfo.discountPercentage) params.set('discount_percentage', checkoutInfo.discountPercentage);
    const query = params.toString();
    return query ? `/login?${query}` : '/login';
  };

  const createDirectCheckout = async (userId, accessToken) => {
    if (!checkoutTierId || !userId) return false;
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const code = (userData.discountCode || '').trim().replace(/^"+|"+$/g, '').toUpperCase();
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/create`,
      { userId, priceId: checkoutTierId, ...(code ? { discountCode: code } : {}) },
      { withCredentials: true, headers }
    );
    if (response.data?.checkoutUrl) {
      window.location.href = response.data.checkoutUrl;
      return true;
    }
    return false;
  };

  const validateBusinessCode = async (code) => {
    if (!code || code.trim() === '' || code.trim().length < 3) {
      setBusinessCodeValid(null);
      setBusinessCodeInfo(null);
      setValidatingCode(false);
      return;
    }

    setValidatingCode(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/businesses/validate-code/${encodeURIComponent(code.trim())}`
        // Removed withCredentials since this is a public endpoint
      );
      if (response.data.valid) {
        setBusinessCodeValid(true);
        setBusinessCodeInfo(response.data.business);
      } else {
        setBusinessCodeValid(false);
        setBusinessCodeInfo(null);
      }
    } catch (error) {
      setBusinessCodeValid(false);
      setBusinessCodeInfo(null);
    } finally {
      setValidatingCode(false);
    }
  };

  useEffect(() => {
    // Debounce business code validation - only validate if code is at least 3 characters
    if (userData.businessCode && userData.businessCode.trim().length >= 3) {
      const timeoutId = setTimeout(() => {
        validateBusinessCode(userData.businessCode);
      }, 800); // Increased debounce time to 800ms
      return () => clearTimeout(timeoutId);
    } else if (userData.businessCode && userData.businessCode.trim().length > 0 && userData.businessCode.trim().length < 3) {
      // Clear validation state if code is too short
      setBusinessCodeValid(null);
      setBusinessCodeInfo(null);
      setValidatingCode(false);
    } else {
      // Clear validation state if code is empty
      setBusinessCodeValid(null);
      setBusinessCodeInfo(null);
      setValidatingCode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.businessCode]);

  const handleBusinessCodeChange = (e) => {
    const code = e.target.value;
    setUserData({...userData, businessCode: code});
  };

  const validateStep = (step) => {
    setError('');
    switch (step) {
      case 1:
        if (!userData.name) {
          setError('Vennligst skriv inn navn');
          return false;
        }
        if (!userData.email) {
          setError('Vennligst skriv inn e-post');
          return false;
        }
        if (!userData.password) {
          setError('Vennligst skriv inn passord');
          return false;
        }
        if (userData.password.length < 8) {
          setError('Passordet må være minst 8 tegn');
          return false;
        }
        if (userData.password !== userData.confirmPassword) {
          setError('Passordene stemmer ikke overens');
          return false;
        }
        return true;
      case 2:
        if (!userData.sex) {
          setError('Vennligst velg kjønn');
          return false;
        }
        if (!userData.age) {
          setError('Vennligst velg alder');
          return false;
        }
        return true;
      case 3:
        if (!userData.level) {
          setError('Vennligst velg løpserfaring');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError('');
  };

  const handleStartTrial = async () => {
    setTrialLoading(true);
    setError('');
    try {
      // Send email from registration to create trial checkout
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/trial-checkout`,
        { email: userData.email, discountCode: userData.discountCode || undefined },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.checkoutUrl) {
        if (response.data.discountWarning) {
          localStorage.setItem('trialDiscountWarning', response.data.discountWarning);
        } else {
          localStorage.removeItem('trialDiscountWarning');
        }
        // Redirect to Stripe checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        setError('Kunne ikke opprette prøveperiode. Vennligst prøv igjen.');
        setTrialLoading(false);
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      setError(error.response?.data?.message || 'Kunne ikke starte prøveperiode. Vennligst prøv igjen.');
      setTrialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) {
      return;
    }

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('signup');
      
      if (!recaptchaToken && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        setError('Kunne ikke verifisere. Vennligst prøv igjen.');
        return;
      }

      // Include honeypot fields (should be empty - bots will fill these)
      const submitData = {
        ...userData,
        discountCode: userData.discountCode || undefined,
        recaptchaToken,
        website: '', // Honeypot field
        email_confirm: '', // Honeypot field
        _submission_time: Date.now().toString()
      };

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, submitData, {
        withCredentials: true
      });
      const data = response.data;
      if (data.success) {
        if (checkoutTierId && data.userId) {
          setAutoCheckoutLoading(true);
          setSuccess('Registrering fullført. Sender deg videre til betaling...');
          try {
            const started = await createDirectCheckout(data.userId, data.token);
            if (started) return;
          } catch (checkoutError) {
            setError('Registrering fullført, men automatisk videresending til betaling feilet. Du kan starte betaling etter innlogging.');
          } finally {
            setAutoCheckoutLoading(false);
          }
        }
        const discountStatusMessage = data.discountCodeRequested
          ? (data.discountCodeCaptured
            ? ` Rabattkode (${data.appliedDiscountCode}) ble lagret.`
            : ' Rabattkode ble ikke lagret (ugyldig eller utløpt).')
          : '';
        setSuccess((data.message || 'Kontoen din er opprettet!') + discountStatusMessage);
        setIsRegistered(true);
        // Don't auto-redirect - let user choose to start trial or go to login
      } else {
        setError(data.message);
      }
    } catch (error) {
      // setError('Registrering feilet');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>Opprett konto</h2>
            <p className="step-description">Fyll inn grunnleggende informasjon</p>
            <input
              type="text"
              placeholder="Navn"
              value={userData.name}
              onChange={(e) => setUserData({...userData, name: e.target.value})}
              required
            />
            <input
              type="email"
              placeholder="Epost"
              value={userData.email}
              onChange={(e) => setUserData({...userData, email: e.target.value})}
              required
            />
            <input
              type="password"
              placeholder="Passord"
              value={userData.password}
              onChange={(e) => setUserData({...userData, password: e.target.value})}
              required
            />
            <input
              type="password"
              placeholder="Bekreft passord"
              value={userData.confirmPassword}
              onChange={(e) => setUserData({...userData, confirmPassword: e.target.value})}
              required
            />
            
            {/* Business Code Field (Optional) */}
            <div className="form-section">
              <label className="form-label optional-label">
                Bedriftskode (valgfritt)
                <span className="optional-badge">Valgfritt</span>
              </label>
              <input
                type="text"
                placeholder="Skriv inn bedriftskode hvis du har en"
                value={userData.businessCode}
                onChange={handleBusinessCodeChange}
                className={`business-code-input ${businessCodeValid === false ? 'invalid' : ''} ${businessCodeValid === true ? 'valid' : ''}`}
              />
              {validatingCode && (
                <p className="validation-message validating">Validerer kode...</p>
              )}
              {businessCodeValid === true && businessCodeInfo && (
                <div className="validation-message valid">
                  <i className="fas fa-check-circle"></i>
                  <span>Gyldig kode! Du blir medlem av {businessCodeInfo.name}</span>
                  {businessCodeInfo.seats_available !== null && (
                    <span className="seats-info">({businessCodeInfo.seats_available} plasser tilgjengelig)</span>
                  )}
                </div>
              )}
              {businessCodeValid === false && userData.businessCode && (
                <p className="validation-message invalid">
                  <i className="fas fa-times-circle"></i>
                  Ugyldig bedriftskode eller ingen plasser tilgjengelig
                </p>
              )}
            </div>

            {userData.discountCode && (
              <div className="validation-message valid" style={{ marginTop: '0.75rem' }}>
                <i className="fas fa-tag"></i>
                <span>Rabattkode fra lenke: {userData.discountCode}</span>
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <h2>Personlig informasjon</h2>
            <p className="step-description">Fortell oss litt om deg</p>
            {/* Sex Selection */}
            <div className="form-section">
              <label className="form-label">Kjønn</label>
              <div className="option-buttons">
                <button
                  type="button"
                  className={`option-button ${userData.sex === 'mann' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, sex: 'mann'})}
                >
                  Mann
                </button>
                <button
                  type="button"
                  className={`option-button ${userData.sex === 'kvinne' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, sex: 'kvinne'})}
                >
                  Kvinne
                </button>
                <button
                  type="button"
                  className={`option-button ${userData.sex === 'annet' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, sex: 'annet'})}
                >
                  Annet
                </button>
              </div>
            </div>

            {/* Age Selection */}
            <div className="form-section">
              <label className="form-label">Alder</label>
              <div className="option-buttons">
                <button
                  type="button"
                  className={`option-button ${userData.age === 'under 35' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, age: 'under 35'})}
                >
                  Under 35
                </button>
                <button
                  type="button"
                  className={`option-button ${userData.age === 'above 35' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, age: 'above 35'})}
                >
                  Over 35
                </button>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <h2>Fortell oss om deg selv</h2>
            <p className="step-description">Du er nesten ferdig! Dette er det siste spørsmålet, vi lover.</p>
            {/* Running Experience Question */}
            <div className="form-section">
              <div className="option-buttons running-experience">
                <button
                  type="button"
                  className={`option-button ${userData.level === '1' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, level: '1'})}
                >
                  Jeg vil starte med å gå og litt løping
                </button>
                <button
                  type="button"
                  className={`option-button ${userData.level === '2' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, level: '2'})}
                >
                  Jeg kan løpe 15 - 20 minutter og vil fortsette
                </button>
                <button
                  type="button"
                  className={`option-button ${userData.level === '3' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, level: '3'})}
                >
                  Jeg foretrekker å løpe 30 - 60 minutter
                </button>
                <button
                  type="button"
                  className={`option-button ${userData.level === '4' ? 'selected' : ''}`}
                  onClick={() => setUserData({...userData, level: '4'})}
                >
                  Jeg foretrekker å løpe 30 - 120 minutter
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const openApp = (e) => {
    e.preventDefault();
    tryOpenAppThenStore('lopeglad://');
  };

  // Show success screen if registered
  if (isRegistered) {
    return (
      <div className="create-user-container">
        <div className="success-screen">
          <div className="success-icon">✓</div>
          <h2 className="success-title">Registrering vellykket!</h2>
          <p className="success-message-text">{success}</p>
          
          {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}
          
          <div className="trial-offer-section" style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--primary-light)', borderRadius: '8px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--primary-dark)' }}>Start din 7-dagers prøveperiode!</h3>
            <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
              Legg til kortet ditt for å låse opp 7 dager med premium tilgang. Du blir ikke belastet før prøveperioden er over.
            </p>
            <button 
              onClick={handleStartTrial}
              disabled={trialLoading}
              className="trial-button"
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: trialLoading ? 'not-allowed' : 'pointer',
                opacity: trialLoading ? 0.7 : 1,
                marginBottom: '1rem'
              }}
            >
              {trialLoading ? 'Laster...' : 'Lås opp prøveperiode'}
            </button>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Du kan også logge inn og starte prøveperioden senere
            </p>
          </div>
          
          <div className="success-actions" style={{ marginTop: '1.5rem' }}>
            <button 
              onClick={openApp}
              className="open-app-button"
              style={{ marginRight: '1rem' }}
            >
              Åpne i appen
            </button>
            <p style={{ fontSize: '0.9rem', color: '#555', marginTop: '1rem', textAlign: 'center' }}>
              Har du ikke appen?{' '}
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
              {' · '}
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>
            </p>
            <button 
              onClick={() => navigate(buildLoginUrlWithCheckoutParams())}
              className="login-button"
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'transparent',
                color: 'var(--primary-color)',
                border: '2px solid var(--primary-color)',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Gå til innlogging
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-user-container">
      <form onSubmit={handleSubmit} className="create-user-form">
        {/* Progress Indicator */}
        <div className="progress-indicator">
          {[...Array(totalSteps)].map((_, index) => (
            <div key={index} className="progress-step">
              <div className={`progress-dot ${currentStep > index + 1 ? 'completed' : ''} ${currentStep === index + 1 ? 'active' : ''}`}>
                {currentStep > index + 1 ? '✓' : index + 1}
              </div>
              {index < totalSteps - 1 && (
                <div className={`progress-line ${currentStep > index + 1 ? 'completed' : ''}`}></div>
              )}
            </div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        {autoCheckoutLoading && <div className="success-message">Omdirigerer til Stripe...</div>}
        {(checkoutTierId || checkoutInfo.priceAmount || checkoutInfo.productName) && (
          <div className="success-message" style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <strong>Registrer før betaling:</strong>{' '}
            Du oppretter konto først, og blir deretter sendt til Stripe for betaling.
            {checkoutInfo.productName && <span> Produkt: {checkoutInfo.productName}</span>}
            {checkoutInfo.tierLabel && <span> ({checkoutInfo.tierLabel})</span>}
            {checkoutInfo.priceAmount && <span> Pris før rabatt: NOK {checkoutInfo.priceAmount}.</span>}
            {checkoutInfo.discountPercentage && checkoutInfo.priceAmount && !Number.isNaN(Number(checkoutInfo.priceAmount)) && !Number.isNaN(Number(checkoutInfo.discountPercentage)) && (
              <span> Med {checkoutInfo.discountPercentage}% rabatt: NOK {(Number(checkoutInfo.priceAmount) * (1 - Number(checkoutInfo.discountPercentage) / 100)).toFixed(2)}.</span>
            )}
            {userData.discountCode && <span> Rabattkode: {userData.discountCode}.</span>}
          </div>
        )}

        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handleBack}
              className="nav-button back-button"
            >
              Tilbake
            </button>
          )}
          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="nav-button next-button"
            >
              Neste
            </button>
          ) : (
            <>
              {/* Honeypot fields - hidden, should be empty */}
              <input
                type="text"
                name="website"
                style={{display: 'none'}}
                tabIndex="-1"
                autoComplete="off"
                value=""
                readOnly
              />
              <input
                type="text"
                name="email_confirm"
                style={{display: 'none'}}
                tabIndex="-1"
                autoComplete="off"
                value=""
                readOnly
              />
              <button type="submit" className="nav-button submit-button">
                Registrer deg
              </button>
            </>
          )}
        </div>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Link
            to={buildLoginUrlWithCheckoutParams()}
            className="create-account-link"
          >
            Allerede bruker? Logg inn i stedet
          </Link>
        </div>

        {/* reCAPTCHA v3 badge - required by Google */}
        {currentStep === totalSteps && process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
          <div className="recaptcha-notice">
            <small>
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              {' '}and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}apply.
            </small>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateUser; 